// js/components/ProductGallery.js
import Delegate from 'ftdomdelegate';
import { ProductVideo } from './product-video.js';
import { ProductModel } from './product-model.js';

export class ProductGallery {
  constructor(element, options) {
    this.element = element;
    this.delegateElement = new Delegate(this.element);
    this.viewInSpaceElement = this.element.querySelector("[data-shopify-xr]");
    this.options = options;
    this.media = {};
    this.previouslySelectedMedia = null;
    this.cosSliderElement = null;
    this._inlineZoom = {
      enabled: false,
      activeSlide: null,
      activeImage: null,
      boundPointerOver: null,
      boundPointerMove: null,
      boundPointerOut: null
    };
    this._zoomNotice = {
      el: null,
      originalParent: null,
      originalNextSibling: null,
      rafId: null
    };
    this._initializeMedia();
    this._attachListeners();
    this._setupInlineImageZoom();
  }

  destroy() {
    for (let mediaId in this.media) {
      if (this.media.hasOwnProperty(mediaId)) {
        this.media[mediaId].destroy();
      }
    }
    if (this._scrollListener) {
      const sliderContainer = this.cosSliderElement?.querySelector('.cos-slider');
      if (sliderContainer) {
        sliderContainer.removeEventListener('scroll', this._scrollListener);
      }
    }

    this._destroyZoomElements();
    if (this._zoomNotice.rafId) {
      cancelAnimationFrame(this._zoomNotice.rafId);
    }
  }

  _attachListeners() {
    this.delegateElement.on("model:played", this._disableDrag.bind(this));
    this.delegateElement.on("video:played", this._disableDrag.bind(this));
    this.delegateElement.on("model:paused", this._enableDrag.bind(this));
    this.delegateElement.on("video:paused", this._enableDrag.bind(this));
    this._setupSliderScrollListener();
  }

  /**
   * This method must be called whenever the variant is changed
   */
  variantHasChanged(newVariant) {
    fastdom.mutate(() => {
      this.productGalleryCellsElements.forEach((cell) => {
        if (cell.hasAttribute("data-group-name")) {
          let groupName = cell.getAttribute("data-group-name");
          this.options["productOptions"].forEach((option, optionIndex) => {
            if (option.toLowerCase() === groupName) {
              if (newVariant[`option${optionIndex + 1}`].toLowerCase() === cell.getAttribute("data-group-value") || newVariant["featured_media"] && newVariant["featured_media"]["id"] === parseInt(cell.getAttribute("data-media-id"))) {
                cell.classList.remove("is-filtered");
              } else {
                cell.classList.add("is-filtered");
              }
            }
          });
        }
      });
      
      if (newVariant && newVariant["featured_media"]) {
        this._scrollToMedia(newVariant["featured_media"]["id"]);
      }
    });
  }

  _initializeMedia() {
    this.cosSliderElement = this.element.querySelector('cos-slider');
    this.productGalleryCellsElements = this.cosSliderElement ? this.cosSliderElement.querySelectorAll('[data-media-id]') : [];
    this.productGalleryCellsElements.forEach((item) => {
      switch (item.getAttribute("data-media-type")) {
        case "external_video":
        case "video":
          this.media[item.getAttribute("data-media-id")] = new ProductVideo(item, this.options["enableVideoLooping"]);
          break;
        case "model":
          this.media[item.getAttribute("data-media-id")] = new ProductModel();
          break;
      }
    });
  }

  _scrollToMedia(mediaId) {
    const mediaElement = this.element.querySelector(`[data-media-id="${mediaId}"]`);
    if (mediaElement) {
      // cos-slider will handle the scrolling
      mediaElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      this._handleMedia(mediaElement);
    }
  }

  _setupSliderScrollListener() {
    if (!this.cosSliderElement) return;
    
    const sliderContainer = this.cosSliderElement.querySelector('.cos-slider');
    if (!sliderContainer) return;

    this._initZoomNotice();

    let scrollTimeout = null;
    this._scrollListener = () => {
      // Update zoom notice immediately while scrolling (no debounce).
      this._scheduleZoomNoticeUpdate();

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      scrollTimeout = setTimeout(() => {
        this._handleSlideChange();
      }, 150);
    };

    sliderContainer.addEventListener('scroll', this._scrollListener, { passive: true });
    
    // Handle initial slide
    this._handleSlideChange();
  }

  _handleSlideChange() {
    if (!this.cosSliderElement) return;
    
    const currentIndex = this.cosSliderElement.getCurrentIndex();
    // Get items using the same method as cos-slider
    const sliderItems = this.cosSliderElement.getSliderItems();
    
    if (currentIndex >= 0 && currentIndex < sliderItems.length) {
      const currentItem = sliderItems[currentIndex];
      if (currentItem && currentItem !== this.previouslySelectedMedia) {
        this._handleMedia(currentItem);
      }
      this._updateZoomNoticeForItem(currentItem);
    }
  }

  _disableDrag() {
    // No-op: cos-slider handles dragging
  }

  _enableDrag() {
    // No-op: cos-slider handles dragging
  }

  /**
   * This method will handle the given media (for now model and video) to do the appropriate actions (such as launching
   * a video for instance)
   */
  _handleMedia(item) {
    let isInitialLoading = this.previouslySelectedMedia === null;
    if (this.previouslySelectedMedia && this.previouslySelectedMedia !== item) {
      switch (this.previouslySelectedMedia.getAttribute("data-media-type")) {
        case "video":
        case "external_video":
        case "model":
          this.media[this.previouslySelectedMedia.getAttribute("data-media-id")].hasBeenDeselected();
      }
      if (this.previouslySelectedMedia.getAttribute("data-media-type") === "model" && this.viewInSpaceElement) {
        this.viewInSpaceElement.setAttribute("data-shopify-model3d-id", this.viewInSpaceElement.getAttribute("data-shopify-model3d-default-id"));
      }
    }
    switch (item.getAttribute("data-media-type")) {
      case "video":
      case "external_video":
      case "model":
        this.media[item.getAttribute("data-media-id")].hasBeenSelected(isInitialLoading);
        break;
    }
    if (item.getAttribute("data-media-type") === "model" && this.viewInSpaceElement) {
      this.viewInSpaceElement.setAttribute("data-shopify-model3d-id", item.getAttribute("data-media-id"));
    }
    this.previouslySelectedMedia = item;
  }

  _destroyZoomElements() {
    if (!this._inlineZoom.enabled) return;

    this.element.removeEventListener("pointerover", this._inlineZoom.boundPointerOver);
    this.element.removeEventListener("pointermove", this._inlineZoom.boundPointerMove);
    this.element.removeEventListener("pointerout", this._inlineZoom.boundPointerOut);

    if (this._inlineZoom.activeSlide) {
      this._inlineZoom.activeSlide.classList.remove("is-zooming");
    }
    if (this._inlineZoom.activeImage) {
      this._inlineZoom.activeImage.style.removeProperty("--product-image-zoom-x");
      this._inlineZoom.activeImage.style.removeProperty("--product-image-zoom-y");
    }

    this._inlineZoom.activeSlide = null;
    this._inlineZoom.activeImage = null;
    this._inlineZoom.enabled = false;
  }

  _isZoomSupported() {
    return window.matchMedia?.("(min-width: 1000px) and (any-hover: hover)")?.matches;
  }

  _setupInlineImageZoom() {
    if (!this.options?.enableImageZoom) return;
    if (this.options?.isQuickView) return;
    if (!this._isZoomSupported()) return;

    const sliderRoot = this.element.querySelector(".product-gallery-slider");
    if (sliderRoot) sliderRoot.dataset.zoomEnabled = "true";

    this._inlineZoom.enabled = true;

    this._inlineZoom.boundPointerOver = this._onInlineZoomPointerOver.bind(this);
    this._inlineZoom.boundPointerMove = this._onInlineZoomPointerMove.bind(this);
    this._inlineZoom.boundPointerOut = this._onInlineZoomPointerOut.bind(this);

    this.element.addEventListener("pointerover", this._inlineZoom.boundPointerOver, { passive: true });
    this.element.addEventListener("pointermove", this._inlineZoom.boundPointerMove, { passive: true });
    this.element.addEventListener("pointerout", this._inlineZoom.boundPointerOut, { passive: true });
  }

  _onInlineZoomPointerOver(event) {
    if (!this._inlineZoom.enabled) return;
    if (event.pointerType && event.pointerType !== "mouse") return;

    const slide = event.target?.closest?.('[data-media-id][data-media-type="image"]');
    if (!slide) return;

    const image = slide.querySelector('img.product-gallery__image[data-zoom]');
    if (!image) return;

    this._inlineZoom.activeSlide = slide;
    this._inlineZoom.activeImage = image;

    slide.classList.add("is-zooming");
    this._updateInlineZoomVars(event, image);
  }

  _onInlineZoomPointerMove(event) {
    if (!this._inlineZoom.enabled) return;
    if (event.pointerType && event.pointerType !== "mouse") return;
    if (!this._inlineZoom.activeSlide || !this._inlineZoom.activeImage) return;

    // Allow panning even when cursor is over empty space in the slide (object-fit: contain).
    if (!this._inlineZoom.activeSlide.contains(event.target)) return;

    this._updateInlineZoomVars(event, this._inlineZoom.activeImage);
  }

  _onInlineZoomPointerOut(event) {
    if (!this._inlineZoom.enabled) return;
    if (event.pointerType && event.pointerType !== "mouse") return;

    const slide = event.target?.closest?.('[data-media-id][data-media-type="image"]');
    if (!slide) return;

    const toElement = event.relatedTarget;
    if (toElement && slide.contains(toElement)) return;

    slide.classList.remove("is-zooming");
    const image = slide.querySelector("img.product-gallery__image");
    if (image) {
      image.style.removeProperty("--product-image-zoom-x");
      image.style.removeProperty("--product-image-zoom-y");
    }

    if (this._inlineZoom.activeSlide === slide) {
      this._inlineZoom.activeSlide = null;
      this._inlineZoom.activeImage = null;
    }
  }

  _updateInlineZoomVars(event, image) {
    const rect = image.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));

    image.style.setProperty("--product-image-zoom-x", `${x * 100}%`);
    image.style.setProperty("--product-image-zoom-y", `${y * 100}%`);
  }

  _initZoomNotice() {
    if (this._zoomNotice.el) return;
    const zoomNotice = this.element.querySelector(".product-gallery__zoom-notice");
    if (!zoomNotice) return;

    this._zoomNotice.el = zoomNotice;
    this._zoomNotice.originalParent = zoomNotice.parentElement;
    this._zoomNotice.originalNextSibling = zoomNotice.nextElementSibling;
  }

  _scheduleZoomNoticeUpdate() {
    if (!this._zoomNotice.el || !this.cosSliderElement) return;
    if (this._zoomNotice.rafId) return;

    this._zoomNotice.rafId = requestAnimationFrame(() => {
      this._zoomNotice.rafId = null;

      const currentIndex = this.cosSliderElement.getCurrentIndex();
      const sliderItems = this.cosSliderElement.getSliderItems();
      const currentItem = sliderItems?.[currentIndex];
      if (currentItem) this._updateZoomNoticeForItem(currentItem);
    });
  }

  _updateZoomNoticeForItem(currentItem) {
    const zoomNotice = this._zoomNotice.el || this.element.querySelector(".product-gallery__zoom-notice");
    if (!zoomNotice) return;

    const mediaType = currentItem?.getAttribute?.("data-media-type") || "";
    const isImage = mediaType === "image";

    // Keep attribute for debugging / optional CSS hooks.
    const sliderRoot = this.element.querySelector(".product-gallery-slider");
    if (sliderRoot) sliderRoot.setAttribute("data-current-media-type", mediaType);

    // Attach the notice to the active slide so it follows the slide and doesn't lag.
    if (isImage) {
      zoomNotice.hidden = false;
      if (zoomNotice.parentElement !== currentItem) {
        currentItem.appendChild(zoomNotice);
      }
      return;
    }

    // Non-image (video/model): hide notice and restore it to its original place.
    zoomNotice.hidden = true;
    if (this._zoomNotice.originalParent && zoomNotice.parentElement !== this._zoomNotice.originalParent) {
      if (this._zoomNotice.originalNextSibling && this._zoomNotice.originalNextSibling.parentElement === this._zoomNotice.originalParent) {
        this._zoomNotice.originalParent.insertBefore(zoomNotice, this._zoomNotice.originalNextSibling);
      } else {
        this._zoomNotice.originalParent.appendChild(zoomNotice);
      }
    }
  }
}
